<template>
    <div>
      <slot
        :results="results"
        :totalResults="totalResults"
        :hasNextPage="hasNextPage"
        :hasPreviousPage="hasPreviousPage"
        :switchPage="switchPage"
        :goToPage="goToPage"
        :tabs="tabs"
        :loading="loading"
        :upcomingSlider="upcomingSlider"
        :eventsTabLink="eventsTabLink"
        :currentPage="currentPage"
        :totalPages="totalPages"
        :paginationPages="paginationPages"
        :previousLink="previousLink"
        :nextLink="nextLink"
    ></slot>
    </div>
  </template>

  <script>
import { defineComponent, onMounted, ref } from 'vue';
import zonedDate from '../utils/zoned-date';
import { format } from 'date-fns';

const now = format(zonedDate(), 'yyyy-MM-dd');

export default defineComponent({
    name: 'SearchResults',
    setup() {
        const queryParam = ref('q');
        const query = ref('');
        const results = ref([]);
        const allResults = ref([]);
        const hasNextPage = ref(false);
        const hasPreviousPage = ref(false);
        const currentPage = ref(1);
        const totalResults = ref(0);
        const filterParam = ref('filter');
        const tabs = ref([]);
        const loading = ref(true);
        const upcomingSlider = ref([]);
        const eventsTabLink = ref(null);
        const totalPages = ref(1);
        const paginationPages = ref([]);
        const previousLink = ref(null);
        const nextLink = ref(null);
        const itemsPerPage = 10;

        const handleUpcomingSearchResults = ((items) => {
            upcomingSlider.value = items.filter(item => item.collectionType === 'events' && item.date > now);
        });

        const processResults = ((items) => {
            results.value = items
                .filter(item => item.collectionType !== 'people')
                .map(item => {
                const data = {
                    id: item.id,
                    title: item.title,
                    collectionType: item.collectionType,
                    url: item.url,
                    description: item.description,
                    image: item.image ? item.image : null,
                    date: item.date ? format(zonedDate(item.date), 'yyyy-MM-dd') : null,
                };

                if (item.image) {
                    data.image.alt = item.image.alt ? item.image.alt : item.title;
                }

                // Process eventInstances for events
                if (item.collectionType === 'events' && item.eventInstances && item.eventInstances.length > 0) {
                    // Get unique venues
                    const venues = [...new Set(item.eventInstances.map(instance => instance.venue?.title).filter(Boolean))];

                    // Get all dates sorted
                    const dates = item.eventInstances
                        .map(instance => instance.startDate)
                        .filter(Boolean)
                        .sort();

                    if (dates.length > 0) {
                        const firstDate = zonedDate(dates[0]);
                        const lastDate = zonedDate(dates[dates.length - 1]);

                        // Format date range
                        if (dates.length > 1 && format(firstDate, 'yyyy-MM-dd') !== format(lastDate, 'yyyy-MM-dd')) {
                            data.prefix = `${format(firstDate, 'd')}-${format(lastDate, 'd MMM')}`;
                        } else {
                            data.prefix = format(firstDate, 'd MMM');
                        }
                    }

                    // Set venue
                    if (venues.length > 0) {
                        data.suffix = venues.join(', ');
                    }
                }

                return data;
            });

            allResults.value = results.value;
            handleUpcomingSearchResults(results.value);
        });

        const getResults = ((url) => {
            // Use below to use LIVE search results
            // url = url.replace('http://127.0.0.1:5555/', 'https://www.writerstheatre.org/');

            fetch(url)
                .then(response => response.json())
                .then(response => {
                    processResults(response.docs);
                    applyFiltering();
                    generateTabs();
                    generatePaginationPages();
                })
                .catch(() => {
                    // Silent error handling - just stop loading
                    loading.value = false;
                })
                .finally(() => {
                    loading.value = false;
                });
        });

        const applyFiltering = (() => {
            const search = new URLSearchParams(window.location.search);
            const filter = search.has(filterParam.value) ? search.get(filterParam.value) : 'all';

            // Filter results based on current tab
            let filteredResults;
            if (filter === 'all') {
                filteredResults = allResults.value;
            } else {
                filteredResults = allResults.value.filter(item => item.collectionType === filter);
            }

            // Calculate pagination for filtered results
            const total = filteredResults.length;
            totalResults.value = total;
            totalPages.value = Math.ceil(total / itemsPerPage) || 1;

            // Ensure current page is within bounds
            if (currentPage.value > totalPages.value) {
                currentPage.value = totalPages.value;
            }

            // Paginate the filtered results
            const startIndex = (currentPage.value - 1) * itemsPerPage;
            const endIndex = startIndex + itemsPerPage;
            results.value = filteredResults.slice(startIndex, endIndex);

            // Update pagination state
            hasPreviousPage.value = currentPage.value > 1;
            hasNextPage.value = currentPage.value < totalPages.value;

            // Generate pagination links
            generatePaginationLinks();
        });

        const getTotalResultsForTab = ((filter) => {
            return allResults.value.filter(item => item.collectionType === filter).length;
        });

        const doSearch = (() => {
            const url = new URL('/api/search', window.location.origin);

            url.searchParams.set('locale', 'en');
            url.searchParams.set('depth', '2');
            url.searchParams.set('page', currentPage.value);
            url.searchParams.set('q', query.value);

            getResults(url.toString());
        });

        const switchPage = ((step) => {
            currentPage.value = parseInt(currentPage.value) + parseInt(step);
            // Update URL
            const url = new URL(window.location);
            url.searchParams.set('page', currentPage.value);
            window.history.pushState({}, '', url);
            // Re-apply filtering and pagination
            applyFiltering();
            generatePaginationPages();
        });

        const goToPage = ((page) => {
            currentPage.value = parseInt(page);
            // Update URL
            const url = new URL(window.location);
            url.searchParams.set('page', currentPage.value);
            window.history.pushState({}, '', url);
            // Re-apply filtering and pagination
            applyFiltering();
            generatePaginationPages();
        });

        const generatePaginationPages = (() => {
            const pages = [];
            const current = currentPage.value;
            const total = totalPages.value;
            const delta = 2; // Show 2 pages before and after current

            for (let i = 1; i <= total; i++) {
                if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
                    pages.push({
                        number: i,
                        current: i === current,
                        link: searchPageURL(i)
                    });
                } else if (pages[pages.length - 1] !== null) {
                    pages.push(null); // Ellipsis
                }
            }

            paginationPages.value = pages;
        });

        const searchPageURL = ((page) => {
            const url = new URL(window.location);
            url.searchParams.set('page', page);
            return url.toString();
        });

        const generatePaginationLinks = (() => {
            if (hasPreviousPage.value) {
                previousLink.value = searchPageURL(currentPage.value - 1);
            } else {
                previousLink.value = null;
            }

            if (hasNextPage.value) {
                nextLink.value = searchPageURL(currentPage.value + 1);
            } else {
                nextLink.value = null;
            }
        });

        onMounted(() => {
            const search = new URLSearchParams(window.location.search);
            query.value = search.has(queryParam.value) ? search.get(queryParam.value) : '';
            currentPage.value = search.has('page') ? parseInt(search.get('page'), 10) : 1;

            doSearch();
        });

        const searchFilterURL = ((filter, page) => {
            const url = new URL(window.location);

            url.searchParams.set(filterParam.value, filter);

            if (page) {
                url.searchParams.set('page', page);
            }

            return url.toString();
        });

        const generateTabs = (() => {
            const search = new URLSearchParams(window.location.search);

            const filterTabs = [
                {
                    filter: 'all',
                    name: 'All',
                    link: searchFilterURL('all', 1),
                    isActive: !search.has(filterParam.value) || search.get(filterParam.value) === 'all',
                    totalResults: allResults.value.length,
                },
                {
                    filter: 'events',
                    name: 'Concerts',
                    link: eventsTabLink.value = searchFilterURL('events', 1),
                    isActive: search.has(filterParam.value) && search.get(filterParam.value) === 'events',
                    totalResults: getTotalResultsForTab('events'),
                },
                {
                    filter: 'posts',
                    name: 'News',
                    link: searchFilterURL('posts', 1),
                    isActive: search.has(filterParam.value) && search.get(filterParam.value) === 'posts',
                    totalResults: getTotalResultsForTab('posts'),
                },
            ];

            tabs.value = filterTabs;
        });

        return {
            results,
            totalResults,
            hasNextPage,
            hasPreviousPage,
            switchPage,
            goToPage,
            tabs,
            loading,
            upcomingSlider,
            eventsTabLink,
            currentPage,
            totalPages,
            paginationPages,
            previousLink,
            nextLink,
        };
    }
});
</script>
